import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { GlobeIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, CalendarIcon, EyeIcon, ChevronRightIcon, MessageSquareIcon, DownloadIcon, XIcon, BookOpenIcon, FileTextIcon, BuildingIcon, CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface ExemptionsProps {
  isTabbed?: boolean;
}
// Sample exemption request data
const exemptionData = [{
  id: 'EXM001',
  program: 'Master of Business Administration',
  university: 'London Business School',
  location: 'London, United Kingdom',
  status: 'under_review',
  submissionDate: '2023-05-10',
  lastUpdated: '2023-05-15',
  decisionDate: '2023-06-30',
  priority: 'high',
  progress: 100,
  exemptionReason: 'Program is not available locally and aligns with national economic priorities in business leadership.',
  notes: 'Your exemption request is currently being reviewed by the Ministry committee.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Program Curriculum',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'University Acceptance Letter',
    status: 'verified'
  }]
}, {
  id: 'EXM002',
  program: 'PhD in Artificial Intelligence',
  university: 'Stanford University',
  location: 'California, USA',
  status: 'approved',
  submissionDate: '2023-04-05',
  lastUpdated: '2023-05-01',
  decisionDate: '2023-05-01',
  priority: 'high',
  progress: 100,
  exemptionReason: 'Program is aligned with national AI strategy and offers specialized research opportunities not available locally.',
  notes: 'Congratulations! Your exemption request has been approved. You may now proceed with your scholarship application.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Program Curriculum',
    status: 'verified'
  }, {
    name: 'Research Proposal',
    status: 'verified'
  }, {
    name: 'University Acceptance Letter',
    status: 'verified'
  }]
}, {
  id: 'EXM003',
  program: 'Bachelor of Fine Arts in Animation',
  university: 'California Institute of the Arts',
  location: 'California, USA',
  status: 'rejected',
  submissionDate: '2023-03-15',
  lastUpdated: '2023-04-10',
  decisionDate: '2023-04-10',
  priority: 'medium',
  progress: 100,
  exemptionReason: 'Specialized program in digital arts that will contribute to the local media industry development.',
  notes: 'We regret to inform you that your exemption request was not approved. Similar programs are available locally or in partner institutions.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Program Curriculum',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'Portfolio',
    status: 'verified'
  }]
}, {
  id: 'EXM004',
  program: 'Master of Public Health',
  university: 'Harvard University',
  location: 'Massachusetts, USA',
  status: 'submitted',
  submissionDate: '2023-05-05',
  lastUpdated: '2023-05-05',
  decisionDate: null,
  priority: 'medium',
  progress: 100,
  exemptionReason: 'Program offers specialized training in epidemiology and global health policy that aligns with national healthcare development goals.',
  notes: 'Your exemption request has been submitted and is pending initial review.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Program Curriculum',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'University Acceptance Letter',
    status: 'verified'
  }]
}, {
  id: 'EXM005',
  program: 'Master of Science in Sustainable Energy',
  university: 'Imperial College London',
  location: 'London, United Kingdom',
  status: 'in_progress',
  submissionDate: null,
  lastUpdated: '2023-05-18',
  decisionDate: null,
  priority: 'high',
  progress: 70,
  exemptionReason: 'Program aligns with national renewable energy strategy and offers specialized training not available locally.',
  notes: 'Please complete your justification statement and upload your university acceptance letter to proceed with your exemption request.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Program Curriculum',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'missing'
  }, {
    name: 'University Acceptance Letter',
    status: 'pending'
  }]
}];
const Exemptions: React.FC<ExemptionsProps> = ({
  isTabbed = false
}) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExemption, setSelectedExemption] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Filter exemptions based on status and search query
  const filteredExemptions = exemptionData.filter(exemption => {
    const matchesStatus = statusFilter === 'all' || exemption.status === statusFilter;
    const matchesSearch = searchQuery === '' || exemption.program.toLowerCase().includes(searchQuery.toLowerCase()) || exemption.university.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  // Format date string
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Get status display information
  const getStatusInfo = status => {
    switch (status) {
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-3961" />
        };
      case 'submitted':
        return {
          label: 'Submitted',
          color: 'bg-gray-100 text-gray-700',
          icon: <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-3962" />
        };
      case 'under_review':
        return {
          label: 'Under Review',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-3963" />
        };
      case 'approved':
        return {
          label: 'Approved',
          color: 'bg-green-100 text-green-700',
          icon: <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3964" />
        };
      case 'rejected':
        return {
          label: 'Rejected',
          color: 'bg-red-100 text-red-700',
          icon: <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3965" />
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-700',
          icon: <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3966" />
        };
    }
  };
  // Get priority badge style
  const getPriorityBadge = priority => {
    return 'bg-gray-100 text-gray-700 border border-gray-200';
  };
  // Get action button based on status
  const getActionButton = exemption => {
    switch (exemption.status) {
      case 'in_progress':
        return {
          label: 'Continue Request',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: 'continue'
        };
      case 'submitted':
      case 'under_review':
        return {
          label: 'Track Status',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: 'track'
        };
      case 'approved':
        return {
          label: 'Apply for Scholarship',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: 'apply'
        };
      case 'rejected':
        return {
          label: 'View Feedback',
          color: 'bg-gray-700 hover:bg-gray-800 text-white',
          action: 'feedback'
        };
      default:
        return {
          label: 'View Details',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: 'view'
        };
    }
  };
  // Handle action button clicks
  const handleActionClick = (exemption, action) => {
    switch (action) {
      case 'continue':
        navigate(`/student/exemptions/new?requestId=${exemption.id}`);
        break;
      case 'track':
      case 'view':
      case 'feedback':
        handleViewDetails(exemption);
        break;
      case 'apply':
        // If approved, they can apply for scholarships
        navigate('/student/scholarships');
        break;
      default:
        handleViewDetails(exemption);
    }
  };
  // Handle view details
  const handleViewDetails = exemption => {
    setSelectedExemption(exemption);
    setShowDetailModal(true);
  };
  // Handle starting a new exemption request
  const handleNewExemption = () => {
    navigate('/student/exemptions/new');
  };
  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedExemption(null);
  };
  const content = <div className="space-y-6" data-id="element-3967">
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-3968">
        <div className="flex flex-col md:flex-row gap-4 justify-between" data-id="element-3969">
          <div className="relative w-full md:w-64" data-id="element-3970">
            <input type="text" placeholder="Search exemption requests..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3971" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-3972" />
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-3973">
            <div className="flex items-center space-x-2" data-id="element-3974">
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('all')} data-id="element-3975">
                All
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'in_progress' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('in_progress')} data-id="element-3976">
                In Progress
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'submitted' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('submitted')} data-id="element-3977">
                Submitted
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'under_review' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('under_review')} data-id="element-3978">
                Under Review
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'approved' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('approved')} data-id="element-3979">
                Approved
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('rejected')} data-id="element-3980">
                Rejected
              </button>
            </div>
            <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-3981">
              <FilterIcon className="h-4 w-4 mr-1.5" data-id="element-3982" />
              More Filters
            </button>
          </div>
        </div>
      </div>
      {/* Exemptions List */}
      {filteredExemptions.length > 0 ? <div className="space-y-4" data-id="element-3983">
          {filteredExemptions.map(exemption => {
        const statusInfo = getStatusInfo(exemption.status);
        const actionButton = getActionButton(exemption);
        return <div key={exemption.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 transition-all" data-id="element-3984">
                <div className="p-5" data-id="element-3985">
                  <div className="flex flex-col md:flex-row justify-between md:items-start gap-4" data-id="element-3986">
                    {/* Exemption Information */}
                    <div className="flex-grow" data-id="element-3987">
                      <div className="flex justify-between items-start" data-id="element-3988">
                        <div data-id="element-3989">
                          <h3 className="text-lg font-medium text-gray-800" data-id="element-3990">
                            {exemption.program}
                          </h3>
                          <div className="flex items-center mt-1" data-id="element-3991">
                            <BuildingIcon className="w-4 h-4 text-gray-500 mr-1" data-id="element-3992" />
                            <p className="text-sm text-gray-600" data-id="element-3993">
                              {exemption.university}
                            </p>
                          </div>
                          <div className="flex items-center mt-1" data-id="element-3994">
                            <GlobeIcon className="w-4 h-4 text-gray-500 mr-1" data-id="element-3995" />
                            <p className="text-sm text-gray-600" data-id="element-3996">
                              {exemption.location}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0" data-id="element-3997">
                          <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${statusInfo.color}`} data-id="element-3998">
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      {/* Exemption Timeline */}
                      {exemption.status !== 'in_progress' && <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-id="element-3999">
                          <div data-id="element-4000">
                            <p className="text-xs text-gray-500" data-id="element-4001">
                              Submitted On
                            </p>
                            <p className="text-gray-700" data-id="element-4002">
                              {formatDate(exemption.submissionDate)}
                            </p>
                          </div>
                          <div data-id="element-4003">
                            <p className="text-xs text-gray-500" data-id="element-4004">
                              Last Updated
                            </p>
                            <p className="text-gray-700" data-id="element-4005">
                              {formatDate(exemption.lastUpdated)}
                            </p>
                          </div>
                          {exemption.decisionDate && <div data-id="element-4006">
                              <p className="text-xs text-gray-500" data-id="element-4007">
                                Decision Date
                              </p>
                              <p className="text-gray-700" data-id="element-4008">
                                {formatDate(exemption.decisionDate)}
                              </p>
                            </div>}
                        </div>}
                      {/* Progress bar for in-progress applications */}
                      {exemption.status === 'in_progress' && <div className="mt-4" data-id="element-4009">
                          <div className="flex items-center justify-between mb-1" data-id="element-4010">
                            <p className="text-sm text-gray-600" data-id="element-4011">
                              Request Progress
                            </p>
                            <p className="text-sm font-medium text-gray-700" data-id="element-4012">
                              {exemption.progress}%
                            </p>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-4013">
                            <div className="bg-primary-500 h-2.5 rounded-full" style={{
                      width: `${exemption.progress}%`
                    }} data-id="element-4014"></div>
                          </div>
                          <p className="mt-2 text-sm text-gray-600" data-id="element-4015">
                            {exemption.notes}
                          </p>
                        </div>}
                      {/* Badges */}
                      <div className="mt-4 flex flex-wrap gap-2" data-id="element-4016">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${getPriorityBadge(exemption.priority)}`} data-id="element-4017">
                          {exemption.priority.charAt(0).toUpperCase() + exemption.priority.slice(1)}{' '}
                          Priority
                        </span>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 md:items-end" data-id="element-4018">
                      <button onClick={() => handleActionClick(exemption, actionButton.action)} className={`px-4 py-2 rounded-md text-sm font-medium ${actionButton.color}`} data-id="element-4019">
                        {actionButton.label}
                      </button>
                      <button onClick={() => handleViewDetails(exemption)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4020">
                        <EyeIcon className="w-4 h-4 mr-1.5" data-id="element-4021" />
                        View Details
                      </button>
                      {/* Additional actions for submitted exemptions */}
                      {(exemption.status === 'submitted' || exemption.status === 'under_review') && <div className="flex gap-2 mt-2" data-id="element-4022">
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-4023">
                            <MessageSquareIcon className="w-4 h-4" data-id="element-4024" />
                          </button>
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-4025">
                            <DownloadIcon className="w-4 h-4" data-id="element-4026" />
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>;
      })}
        </div> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center" data-id="element-4027">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-4028">
            <GlobeIcon className="w-8 h-8 text-gray-400" data-id="element-4029" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2" data-id="element-4030">
            No exemption requests found
          </h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto mb-5" data-id="element-4031">
            {searchQuery ? 'No exemption requests match your search criteria. Try adjusting your filters or search terms.' : "You haven't started any exemption requests yet. If your desired program is not on the approved list, you can request an exemption."}
          </p>
          <button onClick={handleNewExemption} className="h-10 px-4 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center" data-id="element-4032">
            <GlobeIcon className="w-4 h-4 mr-2" data-id="element-4033" />
            Start New Exemption Request
          </button>
        </div>}
      {/* Exemption Details Modal */}
      {showDetailModal && selectedExemption && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4034">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-4035">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal} data-id="element-4036"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-4037">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-4038">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-4039">
                <div className="flex justify-between items-start mb-4" data-id="element-4040">
                  <div data-id="element-4041">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4042">
                      Exemption Request Details
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-4043">
                      {selectedExemption.program} at{' '}
                      {selectedExemption.university}
                    </p>
                  </div>
                  <button onClick={closeDetailModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-4044">
                    <XIcon className="h-6 w-6" data-id="element-4045" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4046">
                  {/* Exemption Information */}
                  <div data-id="element-4047">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4048">
                      Request Information
                    </h4>
                    <div className="space-y-3" data-id="element-4049">
                      <div data-id="element-4050">
                        <span className="text-sm text-gray-500" data-id="element-4051">
                          Request ID:
                        </span>
                        <p className="text-sm text-gray-900 font-medium" data-id="element-4052">
                          {selectedExemption.id}
                        </p>
                      </div>
                      <div data-id="element-4053">
                        <span className="text-sm text-gray-500" data-id="element-4054">Status:</span>
                        <p className="text-sm" data-id="element-4055">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusInfo(selectedExemption.status).color}`} data-id="element-4056">
                            {getStatusInfo(selectedExemption.status).label}
                          </span>
                        </p>
                      </div>
                      <div data-id="element-4057">
                        <span className="text-sm text-gray-500" data-id="element-4058">Priority:</span>
                        <p className="text-sm" data-id="element-4059">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getPriorityBadge(selectedExemption.priority)}`} data-id="element-4060">
                            {selectedExemption.priority.charAt(0).toUpperCase() + selectedExemption.priority.slice(1)}{' '}
                            Priority
                          </span>
                        </p>
                      </div>
                      {selectedExemption.submissionDate && <div data-id="element-4061">
                          <span className="text-sm text-gray-500" data-id="element-4062">
                            Submitted On:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-4063">
                            {formatDate(selectedExemption.submissionDate)}
                          </p>
                        </div>}
                      <div data-id="element-4064">
                        <span className="text-sm text-gray-500" data-id="element-4065">
                          Last Updated:
                        </span>
                        <p className="text-sm text-gray-900" data-id="element-4066">
                          {formatDate(selectedExemption.lastUpdated)}
                        </p>
                      </div>
                      {selectedExemption.decisionDate && <div data-id="element-4067">
                          <span className="text-sm text-gray-500" data-id="element-4068">
                            Decision Date:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-4069">
                            {formatDate(selectedExemption.decisionDate)}
                          </p>
                        </div>}
                    </div>
                  </div>
                  {/* Program Information */}
                  <div data-id="element-4070">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4071">
                      Program Information
                    </h4>
                    <div className="space-y-3" data-id="element-4072">
                      <div data-id="element-4073">
                        <span className="text-sm text-gray-500" data-id="element-4074">Program:</span>
                        <p className="text-sm text-gray-900" data-id="element-4075">
                          {selectedExemption.program}
                        </p>
                      </div>
                      <div data-id="element-4076">
                        <span className="text-sm text-gray-500" data-id="element-4077">
                          University:
                        </span>
                        <p className="text-sm text-gray-900" data-id="element-4078">
                          {selectedExemption.university}
                        </p>
                      </div>
                      <div data-id="element-4079">
                        <span className="text-sm text-gray-500" data-id="element-4080">Location:</span>
                        <p className="text-sm text-gray-900" data-id="element-4081">
                          {selectedExemption.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Exemption Reason */}
                <div className="mt-6" data-id="element-4082">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4083">
                    Exemption Justification
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3" data-id="element-4084">
                    <p className="text-sm text-gray-700" data-id="element-4085">
                      {selectedExemption.exemptionReason}
                    </p>
                  </div>
                </div>
                {/* Documents */}
                <div className="mt-6" data-id="element-4086">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4087">
                    Supporting Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-4088">
                    {selectedExemption.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-id="element-4089">
                        <div className="flex items-center" data-id="element-4090">
                          <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4091" />
                          <span className="text-sm text-gray-700" data-id="element-4092">
                            {doc.name}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${doc.status === 'verified' ? 'bg-gray-100 text-gray-700' : doc.status === 'pending' ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-4093">
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                      </div>)}
                  </div>
                </div>
                {/* Notes */}
                <div className="mt-6" data-id="element-4094">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4095">
                    Notes
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3" data-id="element-4096">
                    <p className="text-sm text-gray-700" data-id="element-4097">
                      {selectedExemption.notes}
                    </p>
                  </div>
                </div>
                {/* Timeline */}
                <div className="mt-6" data-id="element-4098">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4099">
                    Request Timeline
                  </h4>
                  <div className="space-y-6" data-id="element-4100">
                    <div className="relative" data-id="element-4101">
                      <div className="flex items-center" data-id="element-4102">
                        <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4103">
                          <CalendarIcon className="h-5 w-5 text-gray-600" data-id="element-4104" />
                        </div>
                        <div className="ml-4" data-id="element-4105">
                          <h3 className="text-sm font-medium text-gray-900" data-id="element-4106">
                            Request Started
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-4107">
                            {formatDate(new Date(new Date(selectedExemption.lastUpdated).getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4108"></div>
                    </div>
                    {selectedExemption.submissionDate && <div className="relative" data-id="element-4109">
                        <div className="flex items-center" data-id="element-4110">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4111">
                            <CheckIcon className="h-5 w-5 text-gray-600" data-id="element-4112" />
                          </div>
                          <div className="ml-4" data-id="element-4113">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4114">
                              Request Submitted
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4115">
                              {formatDate(selectedExemption.submissionDate)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4116"></div>
                      </div>}
                    {selectedExemption.status === 'under_review' && <div className="relative" data-id="element-4117">
                        <div className="flex items-center" data-id="element-4118">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4119">
                            <ClockIcon className="h-5 w-5 text-gray-600" data-id="element-4120" />
                          </div>
                          <div className="ml-4" data-id="element-4121">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4122">
                              Under Review
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4123">
                              {formatDate(selectedExemption.lastUpdated)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4124"></div>
                      </div>}
                    {(selectedExemption.status === 'approved' || selectedExemption.status === 'rejected') && <div className="relative" data-id="element-4125">
                        <div className="flex items-center" data-id="element-4126">
                          <div className={`h-9 w-9 rounded-full ${selectedExemption.status === 'approved' ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center z-10`} data-id="element-4127">
                            {selectedExemption.status === 'approved' ? <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-4128" /> : <XCircleIcon className="h-5 w-5 text-red-600" data-id="element-4129" />}
                          </div>
                          <div className="ml-4" data-id="element-4130">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4131">
                              Request{' '}
                              {selectedExemption.status === 'approved' ? 'Approved' : 'Rejected'}
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4132">
                              {formatDate(selectedExemption.decisionDate)}
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-4133">
                <button onClick={() => handleActionClick(selectedExemption, getActionButton(selectedExemption).action)} className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${getActionButton(selectedExemption).color}`} data-id="element-4134">
                  {getActionButton(selectedExemption).label}
                </button>
                <button onClick={closeDetailModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-4135">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
  return <>
      {!isTabbed ? <Layout title="My Exemption Requests" persona="Student" data-id="element-4136">
          {content}
        </Layout> : content}
    </>;
};
export default Exemptions;